import React, { useLayoutEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import PriceModule, { type PriceModuleProps } from '@ingka/price-module';
import { useTranslation } from 'react-i18next';
import { mediaQueries, breakpoints } from '../styles/mediaQueries';
import {
  type DisplayPrice,
  type PriceOfferType,
  type PriceParts,
} from '../services/appointment/model';
import { useStateSelector } from '../store/';
import { useDebounce } from '../hooks/useDebounce';
import TextWithPrice from './TextWithPrice';
import colors from '../styles/colors';
import {
  isIkeaMembershipSavings,
  getPriceIntegerValue,
  isNumericString,
  getFreeOrZeroPrice,
} from '../helpers/currencyHelper';

interface ServicePriceProps {
  price: DisplayPrice;
  serviceId: string;
}

type StyledPriceModuleProps = PriceModuleProps & {
  $isBusinessNetworkPrice?: boolean;
  $hasTextPrice?: boolean;
};

const {
  country,
  language,
  services: { loginRequired },
} = CSS_CONFIG;

const StyledPriceModule = styled(PriceModule)<StyledPriceModuleProps>`
  .price-module__primary-currency-price {
    .price__integer,
    .price-module__current-price {
      white-space: normal;
      ${({ $hasTextPrice }) =>
        $hasTextPrice ? 'font-size: 0.875rem;' : 'font-size: 1.2rem;'};
      ${mediaQueries.portraitTabletUp} {
        ${({ $hasTextPrice }) =>
          $hasTextPrice ? 'font-size: 1rem;' : 'font-size: 1.2rem;'};
      }
    }
    ${mediaQueries.portraitTabletUp} {
      flex-direction: column;
      align-items: flex-end;
      text-align: right;
    }
  }
  && {
    .price-module__information {
      margin-bottom: 0;
      ${mediaQueries.portraitTabletUp} {
        text-align: right;
      }
    }
    .price-module__addon {
      color: ${colors.text.default};
    }
    .price-module__addon {
      ${mediaQueries.portraitTabletUp} {
        text-align: right;
      }
    }
    .price-module__addon {
      ${mediaQueries.portraitTabletUp} {
        text-align: right;
      }
    }
    .price-module__addon > span {
      justify-content: flex-start;
      ${mediaQueries.portraitTabletUp} {
        justify-content: flex-end;
      }
    }
    .price-module__comparison-price-label {
      ${mediaQueries.portraitTabletUp} {
        text-align: right;
        margin-right: 0;
      }
    }
    .price-module__price {
      ${mediaQueries.portraitTabletUp} {
        text-align: right;
      }
    }
    .price:not(:first-child) {
      margin-top: 0;
    }
    .service-price__integer-value-custom {
      margin-right: 0;
    }
    .commercial-message--subtle--family {
      ${({ $isBusinessNetworkPrice }) =>
        $isBusinessNetworkPrice ? `color: ${colors.text.black}` : ''};
    }
  }
`;

const getPriceOfferTypeForPriceModule = (priceOfferType: PriceOfferType) => {
  if (isIkeaMembershipSavings(priceOfferType)) {
    return country === 'ro' ? 'family-romania' : 'family';
  }
  return 'tro';
};

const showLoginLabel = (id: string, isLoggedIn: boolean) => {
  return !isLoggedIn && loginRequired.some((serviceId) => serviceId === id);
};

const getCommercialMessageProps = (
  t: ReturnType<typeof useTranslation>['t'],
  priceOfferType?: PriceOfferType,
) => {
  if (isIkeaMembershipSavings(priceOfferType)) {
    return {
      message:
        priceOfferType === 'family'
          ? t('components.servicePrice.familyPriceLabel')
          : t('components.servicePrice.businessNetworkPriceLabel'),
    };
  }
};

const getPriceProps = (price: PriceParts) => {
  return {
    ...price,
    className: 'service-price__integer-value-custom',
  };
};

const getComparisonPriceProps = (captionPrefix: string, price?: PriceParts) => {
  if (price) {
    const priceProps =
      typeof price === 'string' ? { integerValue: price } : price;
    return {
      ...priceProps,
      className: 'service-price__integer-value-custom',
      captionPrefix,
    };
  }
};

const getCurrentPriceStringValue = (priceParts: PriceParts): string => {
  return typeof priceParts === 'string'
    ? priceParts
    : getPriceIntegerValue(priceParts);
};

const ServicePrice: React.FC<ServicePriceProps> = ({ price, serviceId }) => {
  const { t } = useTranslation();
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const isLoggedIn = useStateSelector((state) => state.user.isLoggedIn);
  const {
    priceOfferType,
    exclSavings,
    exclSavingsFormatted,
    inclSavings,
    inclSavingsFormatted,
    validFromDateUTC,
    validToDateUTC,
  } = price;
  const debouncedSetScreenWith = useDebounce(
    () => setScreenWidth(window.innerWidth),
    100,
  );

  useLayoutEffect(() => {
    window.addEventListener('resize', debouncedSetScreenWith);
    return () => window.removeEventListener('resize', debouncedSetScreenWith);
    // Adding eventlistener only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceAddons = useMemo(() => {
    const addons: React.ReactNode[] = [];

    if (priceOfferType) {
      if (isIkeaMembershipSavings(priceOfferType)) {
        addons.push(
          <TextWithPrice
            key="regularPriceContainer"
            alignment="right"
            price={exclSavingsFormatted}
            textKey="components.servicePrice.regularPriceLabelWithPrice"
          />,
        );
      }

      if (validFromDateUTC && validToDateUTC) {
        const locale = `${language}-${country.toUpperCase()}`;
        const options: Intl.DateTimeFormatOptions = {
          month: 'short',
          day: 'numeric',
        };
        const validFromToLabel = t('components.servicePrice.validFromTo', {
          fromToDateRange: new Intl.DateTimeFormat(locale, options).formatRange(
            new Date(validFromDateUTC),
            new Date(validToDateUTC),
          ),
        });

        if (priceOfferType === 'family' || priceOfferType === 'ibn') {
          addons.push(validFromToLabel);
        } else if (screenWidth > breakpoints.landscapePhoneUp) {
          addons.push(
            t('components.servicePrice.timeRestrictedOfferWithValidFromTo', {
              validFromTo: validFromToLabel,
            }),
          );
        } else {
          addons.push(t('components.servicePrice.timeRestrictedOffer'));
          addons.push(validFromToLabel);
        }
      }
    }

    if (showLoginLabel(serviceId, isLoggedIn)) {
      addons.push(`${t('pages.services.loginText')}`);
    }

    return addons;
  }, [
    exclSavingsFormatted,
    priceOfferType,
    validFromDateUTC,
    validToDateUTC,
    isLoggedIn,
    screenWidth,
    serviceId,
    t,
  ]);

  const currentPriceValue = priceOfferType
    ? getFreeOrZeroPrice(
        inclSavings,
        inclSavingsFormatted.mainPrice,
        'components.servicePrice.freePriceOffer',
        CSS_CONFIG,
        t,
      )
    : getFreeOrZeroPrice(
        exclSavings,
        exclSavingsFormatted.mainPrice,
        'components.servicePrice.freeRegularPrice',
        CSS_CONFIG,
        t,
      );

  const hasTextPrice = !isNumericString(
    getCurrentPriceStringValue(currentPriceValue),
  );

  return priceOfferType ? (
    <StyledPriceModule
      $isBusinessNetworkPrice={priceOfferType === 'ibn'}
      $hasTextPrice={hasTextPrice}
      productName=""
      commercialMessageProps={getCommercialMessageProps(t, priceOfferType)}
      currentPriceProps={getPriceProps(
        getFreeOrZeroPrice(
          inclSavings,
          inclSavingsFormatted.mainPrice,
          'components.servicePrice.freePriceOffer',
          CSS_CONFIG,
          t,
        ),
      )}
      comparisonPriceLabel={t('components.servicePrice.regularPriceLabel')}
      comparisonPriceProps={getComparisonPriceProps(
        t('components.servicePrice.regularPriceLabel'),
        exclSavingsFormatted.mainPrice,
      )}
      secondaryCurrencyPriceProps={
        inclSavingsFormatted.secondaryPrice
          ? getPriceProps(
              getFreeOrZeroPrice(
                inclSavings,
                inclSavingsFormatted.secondaryPrice,
                'components.servicePrice.freePriceOffer',
                CSS_CONFIG,
                t,
              ),
            )
          : undefined
      }
      secondaryCurrencyComparisonPriceProps={getComparisonPriceProps(
        t('components.servicePrice.regularPriceLabel'),
        exclSavingsFormatted.secondaryPrice,
      )}
      priceOfferType={getPriceOfferTypeForPriceModule(priceOfferType)}
      size="xsmall"
      priceAddons={priceAddons}
      data-cy={`specialPriceModule-${serviceId}`}
    />
  ) : (
    <StyledPriceModule
      $hasTextPrice={hasTextPrice}
      productName=""
      currentPriceProps={getPriceProps(
        getFreeOrZeroPrice(
          exclSavings,
          exclSavingsFormatted.mainPrice,
          'components.servicePrice.freeRegularPrice',
          CSS_CONFIG,
          t,
        ),
      )}
      secondaryCurrencyPriceProps={
        exclSavings > 0 && exclSavingsFormatted.secondaryPrice
          ? getPriceProps(exclSavingsFormatted.secondaryPrice)
          : undefined
      }
      size="xsmall"
      priceAddons={priceAddons}
      data-cy={`regularPriceModule-${serviceId}`}
    />
  );
};

export default ServicePrice;
